import React from 'react'

import SEO from 'src/components/seo'
import { Trans, withTranslation } from 'gatsby-plugin-react-i18next'
import { graphql } from 'gatsby'

import {
  Container,
  Subtitle,
  Text,
  ALink,
  SubHeader,
  PageTitle,
  Highlight,
  ListItem,
  SimpleListItem,
  PrivacyOrderedList,
  PrivacyUnorderedList,
  OverflowContainer,
} from '../templates/ShopifyPage/styles'
import TosNav from '../components/TosNav'

const PrivacyPolicy = () => (
  <>
    <SEO
      title="Privacy Statement"
      description="The Skylight Frame (Glimpse LLC) Privacy Statement."
      path="/privacy-policy/"
    />
    <Container>
      <TosNav currentRoutePath="/privacy-policy/" />
      <OverflowContainer>
        <PageTitle>
          <Trans>Skylight Privacy Statement</Trans>
        </PageTitle>
        <Subtitle>
          <Trans i18nKey="auto_translate_privacy" />
          <br />
          <Trans>Effective Date: May 20, 2025</Trans>
        </Subtitle>
        <Text>
          <Trans>
            This Privacy Statement ("Privacy Statement") describes how Glimpse LLC, doing business
            as Skylight ("Skylight", "we", "us" and "our") collects, uses, stores, shares and
            discloses your personal data when your use our Websites, mobile applications, digital
            frames, smart calendars, and other products and services that link to this Privacy
            Statement (collectively referred to here as the "Services").
          </Trans>
        </Text>
        <Text>
          <Trans>
            This statement also provides required disclosures under applicable law (see Additional
            Legal Disclosures section below) and serves as our Notice at Collection under the
            California Privacy Rights Act.
          </Trans>
        </Text>
        <Text>
          <Trans>
            If you are visiting this Website or using our Services from the European Union, the
            United Kingdom, or elsewhere in the European Economic Area, please see the included
            Supplemental European Privacy Statement.
          </Trans>
        </Text>

        <SubHeader>
          <Trans>Data We Collect and How We Use It</Trans>
        </SubHeader>
        <Text>
          <Trans>
            Skylight collects personal data from you and about you depending on how you interact
            with our Services. For example:
          </Trans>
        </Text>
        <Text>
          <Trans>
            When you register for an account with us to use our digital frame or smart calendar
            products, you provide us with your name, address, contact information, billing
            information, names and contact information of friends and relatives you invite through
            your account, photos, calendar events, and other personal data you provide to:
          </Trans>
        </Text>
        <PrivacyOrderedList>
          <SimpleListItem>
            <Trans>
              Provide you with the Services and monitor what Services you have purchased
            </Trans>
          </SimpleListItem>
          <SimpleListItem>
            <Trans>Contact you for administrative purposes such as customer support</Trans>
          </SimpleListItem>
          <SimpleListItem>
            <Trans>Reply to inquiries via a customer support request</Trans>
          </SimpleListItem>
          <SimpleListItem>
            <Trans>
              Send communications, including updates on promotions and events, relating to products
              and services offered by us, if you have provided your consent and not withdrawn it.
            </Trans>
          </SimpleListItem>
        </PrivacyOrderedList>
        <Text>
          <Trans>
            Whenever you use our Services, we collect data and information made available on your
            device, such as your device Internet Protocol ("IP") address, device name, operating
            system version, the configuration of our mobile app when utilizing our Services, the
            time and date of your use of the Service, and other statistics to
          </Trans>
        </Text>
        <PrivacyOrderedList>
          <SimpleListItem>
            <Trans>Understand and analyze the usage trends and preferences of our users</Trans>
          </SimpleListItem>
          <SimpleListItem>
            <Trans>
              Improve the Service, and to develop new products, services, feature, and functionality
            </Trans>
          </SimpleListItem>
          <SimpleListItem>
            <Trans>
              To investigate and take action with respect to any illegal activity such as
              unauthorized access or fraud.
            </Trans>
          </SimpleListItem>
        </PrivacyOrderedList>

        <SubHeader>
          <Trans>Protection of Your Photographs and Videos</Trans>
        </SubHeader>
        <Text>
          <Trans>
            We protect your photo and video content with industry standard security measures,
            including:
          </Trans>
        </Text>
        <PrivacyUnorderedList>
          <ListItem>
            <Trans>
              Photo and video content you provide is stored on servers with our secure cloud hosting
              provider.
            </Trans>
          </ListItem>
          <ListItem>
            <Trans>
              Photos and videos are sent to your Skylight device using an encrypted protocol and are
              not accessible to any other party.
            </Trans>
          </ListItem>
          <ListItem>
            <Trans>
              We will never share your content unless you give us clear instructions through the
              Services to do so, such as when you select family members or others to receive content
              on their frames.
            </Trans>
          </ListItem>
        </PrivacyUnorderedList>

        <SubHeader>
          <Trans>Third-Party Services</Trans>
        </SubHeader>
        <Text>
          <Trans>
            If you choose to use or link our Services with third-party services, such as synching
            your online accounts with our smart family calendar, or using an Amazon Fire device with
            our Skylight Digital app, we will use information we collect to authenticate you,
            provide the Services you request, and we will transmit data to and from the third-party
            service you wish to use with our Skylight Services based on your selections.
          </Trans>
        </Text>
        <Text>
          <Trans>
            Third parties whose services you use in connection with ours, or third parties whose
            websites we link to from our Websites, will have data information practices that are
            different from ours. This Skylight Privacy Statement does not apply to the activities of
            third parties when they are collecting or using data from you for their own purpose or
            on behalf of others. We cannot control and are not responsible for the activities of
            these third parties. We encourage you to review their privacy policies to understand how
            they use your information.
          </Trans>
        </Text>

        <SubHeader>
          <Trans>Cookies and Analytics Information</Trans>
        </SubHeader>
        <Text>
          <Trans>
            We may use various technical mechanisms such as cookies, web beacons, and similar
            tracking technologies to monitor how users use our Website and mobile apps to better
            understand customer behavior and improve our Services. Cookies are files with a small
            amount of data that are commonly used as anonymous unique identifiers. These are sent to
            your browser from the websites that you visit and are stored on your device’s internal
            memory.
          </Trans>
        </Text>
        <Text>
          <Trans>
            You can delete and disable all cookies through settings in your Internet browser,
            however if you choose to do so, some components of our Services may not work properly if
            cookies necessary for operation are blocked.
          </Trans>
        </Text>

        <SubHeader>
          <Trans>When We Disclose Data</Trans>
        </SubHeader>
        <Text>
          <Trans>
            Except as described in this Privacy Statement, we will not disclose Personal Data that
            we collect on the Services to third parties without your consent. We never sell your
            Personal Data to any third party. However, we do share your data with third parties for
            digital advertising purposes as described below.
          </Trans>
        </Text>
        <Text>
          <Trans>
            We may disclose information to third parties in the following circumstances:
          </Trans>
        </Text>

        <Text>
          <Highlight>
            <Trans>Third-Party Vendors.</Trans>{' '}
          </Highlight>
          <Trans>
            We use vendor companies to help us provide our Services. We enter into written
            agreements with these vendors requiring them to retain, share, store or use Personal
            Data for solely to fulfill your order and otherwise help us provide the Services to you.
          </Trans>
        </Text>
        <Text>
          <Highlight>
            <Trans>Third-Party Services.</Trans>{' '}
          </Highlight>
          <Trans>
            As noted above, if you choose to link our Services with a third-party service such as
            your online calendar, we will provide information to that third party based on your
            settings in our Services in order to interoperate with the third-party service.
          </Trans>
        </Text>
        <Text>
          <Highlight>
            <Trans>Digital Advertising.</Trans>{' '}
          </Highlight>
          <Trans>
            We partner with third parties to manage our advertising on other sites to deliver ads
            that are more relevant to you. From time to time, we may confidentially share email
            addresses with our partners for ad targeting. Our third-party partners may use cookies
            or similar technologies to provide you with advertising based upon your browsing
            activities and interests. If you do not want us to share your data for digital
            advertising, please email us at
          </Trans>{' '}
          <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink>.{' '}
          <Trans>
            Please note that after we process your opt-out you may still receive digital advertising
            for our products but they will no longer be personalized using your information. To
            learn more about your choices for receiving interest-based advertising or other ways to
            opt-out, please review the information below:
          </Trans>
        </Text>
        <PrivacyUnorderedList>
          <ListItem>
            <Trans>
              DAA: To opt-out of such collection and use for interest-based advertising by the
              Digital Advertising Alliance (DAA) participating companies, please visit
            </Trans>{' '}
            <ALink href="https://www.aboutads.info/choices">
              <Trans>the DAA's website.</Trans>
            </ALink>
          </ListItem>
          <ListItem>
            <Trans>
              EDAA: To opt-out from the use of information about your online activities for
              interest-based advertising by European Interactive Digital Advertising Alliance (EDAA)
              member companies, please visit
            </Trans>{' '}
            <ALink href="https://www.youronlinechoices.eu/">
              <Trans>the EDAA's website.</Trans>
            </ALink>
          </ListItem>
          <ListItem>
            <Trans>
              NAI: To opt-out from the use of information about your online activities for
              interest-based advertising by Network Advertising Initiative (NAI) member companies,
              please visit
            </Trans>{' '}
            <ALink href="https://www.networkadvertising.org/choices/">
              <Trans>the NAI's website.</Trans>
            </ALink>
          </ListItem>
        </PrivacyUnorderedList>

        <Text>
          <Highlight>
            <Trans>Legal Reasons.</Trans>{' '}
          </Highlight>
          <Trans>
            When required by law or necessary to avoid harm. We may share your information if we are
            required to do so by law or in the good-faith belief that such action is necessary to
            comply with applicable laws, to protect our property, to prevent fraud, or in response
            to a court order, judicial or other government subpoena, or to otherwise cooperate with
            law enforcement or other governmental agencies. This may include sharing information
            with other companies, lawyers, agents, or government agencies.We may also share your
            information if we believe it is necessary to protect you or others from harm, such as
            fraud or abuse, or as needed to establish our legal rights.
          </Trans>
        </Text>
        <Text>
          <Highlight>
            <Trans>Corporate Events.</Trans>{' '}
          </Highlight>
          <Trans>
            If Skylight (or our assets related to our business) are acquired by another company,
            whether by merger, acquisition, or otherwise, that company will receive all information
            gathered by Skylight on the Websites and Service. In this event, you will be notified
            via email and/or a prominent notice on our Website of any change in ownership, uses of
            your Personal Data, and choices you may have regarding your Personal Data.
          </Trans>
        </Text>

        <SubHeader>
          <Trans>Security</Trans>
        </SubHeader>
        <Text>
          <Trans>
            We value your trust in providing us your Personal Data. We implement appropriate
            physical, technical and organizational security measures to protect Personal Data
            against accidental or unlawful destruction, accidental loss or alteration, unauthorized
            disclosure or access, and against all other unlawful forms of use, in accordance with
            industry standards. Despite our efforts we want to remind you that no method of
            transmission over the internet, or method of electronic storage, is 100% secure and
            reliable, and we cannot guarantee its absolute security. It is important that you take
            steps to protect your information as well, such as protecting your passwords.
          </Trans>
        </Text>
        <Text>
          <Trans>
            We do our very best to make sure that our personnel and trusted hosting and other
            service providers who need the information to perform a specific job are granted access
            to the Personal Data only on a need-to-know basis and are subject to stringent
            confidentiality requirements.
          </Trans>
        </Text>

        <SubHeader>
          <Trans>Children's Privacy</Trans>
        </SubHeader>
        <Text>
          <Trans>
            Our Services are not designed or intended for use by anyone under the age of 13, and you
            may not use the Services if you are under the age of 13. As such, we do not knowingly
            collect personal information from children under 13. If you are a parent or guardian and
            you are aware that your child has provided us with personal information, please contact
            us so that we will be able to take the necessary actions.
          </Trans>
        </Text>

        <SubHeader>
          <Trans>Supplemental European Privacy Statement</Trans>
        </SubHeader>
        <Text>
          <Trans>
            This Supplemental European Privacy Statement is relevant to residents of the European
            Economic Area ("EEA"), European Union, United Kingdom, or Switzerland who uses the
            Services. Applicable law, including the European Union Regulation 2016/679 of 27 April
            2016 on the protection of natural persons with regard to the processing of personal data
            and on the free movement of such data ("GDPR"), the UK GDPR and the Swiss Federal Act on
            Data Protection require Skylight to provide additional information about its data
            processing practices.
          </Trans>
        </Text>
        <Text>
          <Trans>
            For purposes of the GDPR and other applicable laws, we, Glimpse LLC, with offices at
            101A Clay St #144, San Francisco, California 94111, USA, are the data controller of your
            personal information that we collect.
          </Trans>
        </Text>

        <Text>
          <Highlight>
            <Trans>Personal Data Transfers under our Data Privacy Framework.</Trans>{' '}
          </Highlight>
          <Trans>
            Glimpse LLC, (d/b/a “Skylight”) has certified its participation in the EU-U.S. Data
            Privacy Framework, the UK Extension to the EU- US Data Privacy Framework and the
            Swiss-U.S. Data Privacy Framework (collectively, the "DPF") with the U.S. Department of
            Commerce. This certification relates to Skylight’s processing of the personal
            information categories described in this Privacy Statement and received from the
            European Economic Area (“EEA”), the United Kingdom and Switzerland under the DPF in
            connection with our certification. If there is any conflict between the terms in this
            Disclosure and the DPF Principles, the Principles shall govern. To learn more about the
            DPF, or to review our certification on the DPF list, visit the
          </Trans>{' '}
          <ALink href="https://www.dataprivacyframework.gov/">DPF website.</ALink>{' '}
          <Trans>
            In accordance with the DPF, Skylight is also liable for onward transfers to third
            parties that process personal information in a way that does not follow the DPF unless
            Skylight was not responsible for the event giving rise to any alleged damage.
          </Trans>
        </Text>

        <Text>
          <Trans>Please email</Trans>{' '}
          <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink>{' '}
          <Trans>
            with any questions or concerns about our DPF certification. Skylight has a policy of
            responding to individuals within forty-five (45) days of an inquiry or complaint.
          </Trans>
        </Text>
        <Text>
          <Trans>
            Skylight commits to refer unresolved complaints concerning our handling of personal
            information received in reliance on the EU-U.S. DPF and the UK Extension to the EU- U.S.
            DPF and the Swiss-U.S. DPF to the International Centre for Dispute Resolution/American
            Arbitration Association (“ICDR/AAA”), an alternative dispute resolution provider based
            in the United States. If you do not receive timely acknowledgment of your DPF
            Principles-related complaint from us, or if we have not addressed your DPF
            Principles-related complaint to your satisfaction, please visit
          </Trans>{' '}
          <ALink href="https://go.adr.org/dpf_irm.html">https://go.adr.org/dpf_irm.html</ALink>{' '}
          <Trans>
            for more information or to file a complaint. The services of ICDR/AAA are provided at no
            cost to you.
          </Trans>
        </Text>
        <Text>
          <Trans>
            You may have the option to select binding arbitration under the applicable Data Privacy
            Framework Panel for the resolution of your complaint under certain circumstances.
            Skylight’s compliance with the DPF is subject to the investigatory and enforcement
            powers of the U.S. Federal Trade Commission.
          </Trans>
        </Text>

        <Text>
          <Highlight>
            <Trans>Data Retention.</Trans>{' '}
          </Highlight>
          <Trans>
            Skylight keeps Personal Data for as long as required to provide the Services you have
            requested or registered for and to comply with applicable laws and record-keeping
            requirements.
          </Trans>
        </Text>

        <Text>
          <Highlight>
            <Trans>Data Subject Rights.</Trans>{' '}
          </Highlight>
          <Trans>
            Skylight has implemented methods for you to exercise your privacy rights including your
            rights access to and rectification or erasure of your Personal Data or restriction of
            processing concerning you, as well as the right to data portability under the GDPR. You
            also have the right to object, on grounds relating to your situation, at any time to the
            processing of your Personal Data by us and we can be required to no longer process your
            Personal Data. In general, you have the right to object to our processing of your
            Personal Data for direct marketing purposes. If you have a right to object and you
            exercise this right, your Personal Data will no longer be processed for such purposes by
            us. You can exercise such rights by accessing the information in your account and
            privacy settings, if applicable, or submitting a request by email to
          </Trans>{' '}
          <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink>.
        </Text>
        <Text>
          <Trans>
            If you have provided consent for cookies that are not strictly necessary, or for direct
            marketing emails or other data processing based on your consent, you have the right to
            withdraw your consent at any time, without affecting the lawfulness of processing based
            on consent before its withdrawal. You have the right to lodge a complaint with a
            supervisory authority.
          </Trans>
        </Text>

        <Text>
          <Highlight>
            <Trans>Your Choices.</Trans>{' '}
          </Highlight>
          <Trans>
            You are not required to provide any Personal Data to Skylight, but if you do not provide
            any Personal Data at all to Skylight, we will not be able to provide the Services. If
            you provide only some of the personal information requested, the functionality of some
            Services designed to process certain information for you may not operate correctly.
          </Trans>
        </Text>

        <Text>
          <Highlight>
            <Trans>Profiling.</Trans>{' '}
          </Highlight>
          <Trans>
            Skylight does not use in connection with the Services automated decision-making,
            including profiling, in a way that produces legal effects concerning you or which
            significantly affects you.
          </Trans>
        </Text>

        <SubHeader>
          <Trans>Additional Information for California Residents</Trans>
        </SubHeader>
        <Text>
          <Highlight>
            <Trans>How We Source, Use, and Disclose Information for Business Purposes.</Trans>{' '}
          </Highlight>
          <Trans>
            The following chart details the categories of personal information we collect, the
            sources of such personal information, and how we use and share such information for
            business purposes.
          </Trans>
        </Text>

        <Text>
          <Highlight>
            <Trans>Your California Privacy Rights.</Trans>{' '}
          </Highlight>
          <Trans>
            If you are a California resident, the California Consumer Privacy Act (“CCPA”) allows
            you to make certain requests about your personal information. Specifically, the CCPA
            allows you to request us to:
          </Trans>
        </Text>

        <PrivacyOrderedList>
          <SimpleListItem>
            <Trans>
              Inform you about the categories of personal information we collect or disclose about
              you; the categories of sources of such information; the business or commercial purpose
              for collecting your personal information; and the categories of third parties with
              whom we share/disclose personal information
            </Trans>
          </SimpleListItem>
          <SimpleListItem>
            <Trans>
              Provide access to and/or a copy of certain personal information we hold about you
            </Trans>
          </SimpleListItem>
          <SimpleListItem>
            <Trans>Delete certain personal information we have about you</Trans>
          </SimpleListItem>
          <SimpleListItem>
            <Trans>
              Provide you with information about the financial incentives that we offer to you, if
              any.
            </Trans>
          </SimpleListItem>
        </PrivacyOrderedList>

        <Text>
          <Trans>
            The CCPA further provides you with the right not to be discriminated against (as
            provided for in applicable law) for exercising your rights under the statute. Please
            note that certain information may be exempt from such requests under California law. For
            example, we need certain information in order to provide our Services to you. We also
            will take reasonable steps to verify your identity before responding to a request for
            security purposes. In doing so, we may ask you for verification information so that we
            can match at least two verification points with information we maintain in our files
            about you. If we are unable to verify you through this method, we shall have the right,
            but not the obligation, to request additional information from you.
          </Trans>
        </Text>
        <Text>
          <Trans>
            If you would like further information regarding your legal rights under California law
            or would like to exercise any of them, or if you are an authorized agent making a
            request on a California consumer's behalf, please contact us at
          </Trans>{' '}
          <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink>.
        </Text>
        <Text>
          <Trans>
            The CCPA provides certain rights if a company “sells” personal information, as such term
            is defined under the CCPA. We do not engage in activities that would be considered
            “sales” of your personal information under the CCPA.
          </Trans>
        </Text>

        <Text>
          <Highlight>
            <Trans>Shine the Light Disclosure.</Trans>{' '}
          </Highlight>
          <Trans>
            The California “Shine the Light” law gives residents of California the right under
            certain circumstances to request information from us regarding the manner in which we
            share certain categories of personal information (as defined in the Shine the Light law)
            with third parties for their direct marketing purposes. We do not share your personal
            information with third parties for their own direct marketing purposes without your
            affirmative consent.
          </Trans>
        </Text>

        <Text>
          <Highlight>
            <Trans>Browser “Do Not Track” Signals.</Trans>{' '}
          </Highlight>
          <Trans>
            Some web browsers may transmit “do-not-track” signals to a website. Because of
            differences in how web browsers incorporate and activate this feature, it is not always
            clear whether users intend for these signals to be transmitted, or whether they are
            aware of them. Because there currently is no industry standard concerning what, if
            anything, a service should do when they receive such signals, we currently do not take
            action in response to these signals.
          </Trans>
        </Text>

        <SubHeader>
          <Trans>Changes to This Privacy Statement</Trans>
        </SubHeader>
        <Text>
          <Trans>
            We may update this Privacy Statement from time to time and if we do, we'll post any
            changed on this page. These changes are effective immediately. If you continue to use
            our Websites and our Services after those changes are in in effect, you agree to the new
            statement. If the changes are significant, we may provide more prominent notice or get
            your consent, as required by law.
          </Trans>
        </Text>

        <SubHeader>
          <Trans>Account Information and Deletion</Trans>
        </SubHeader>
        <Text>
          <Trans>
            You may update, correct, or delete your Personal Data and preferences at any time by
            contacting us at
          </Trans>{' '}
          <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink>.{' '}
          <Trans>
            Please note that while any changes you make will be reflected in active user databases
            instantly or within a reasonable period of time, we may retain some information you
            submit for satisfaction of legal obligations, or where we otherwise reasonably believe
            that we have a legitimate reason to do so.
          </Trans>
        </Text>

        <SubHeader>
          <Trans>Contact Us</Trans>
        </SubHeader>
        <Text>
          <Trans>
            If you have any questions or suggestions about our Privacy Statement, do not hesitate to
            contact us at
          </Trans>{' '}
          <ALink href="mailto:help@myskylight.com">help@myskylight.com</ALink>{' '}
          <Trans>
            or write to us at Glimpse LLC, 101A Clay St #144, San Francisco, California 94111, USA.
          </Trans>
        </Text>
      </OverflowContainer>
    </Container>
  </>
)

export default withTranslation()(PrivacyPolicy)

export const query = graphql`
  query ($language: String!) {
    locales: allLocale(filter: { ns: { in: ["common", "privacy"] }, language: { eq: $language } }) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`
